import React, { ComponentType, PropsWithChildren, Reducer, useEffect, useImperativeHandle, useReducer } from 'react';
import { IModalDescription, IModalManager, IModalProps, ModalIdentifier, ModalStat, ViewVariant } from '@cp/shared/modal';
import { MODAL_MOBILE_BREAKPOINT_THRESHOLD, ModalLayoutContext } from '@cp/ds/src/components/modalLayout';
import { useMediaQuery } from '@mui/material';
import { IModalWrapperProps } from './modal.interface';
import { DrawerModal, withAnchor } from './drawerModal';
import { DialogModal } from './dialogModal';
// import { FullscreenModal } from './fullscreenModal';

const MODAL_LAYOUTS: Record<ViewVariant, ComponentType<PropsWithChildren & IModalWrapperProps>> = {
  [ViewVariant.Dialog]: DialogModal,
  [ViewVariant.DrawerBottom]: withAnchor(DrawerModal, 'bottom'),
  [ViewVariant.DrawerRight]: withAnchor(DrawerModal, 'right'),
  [ViewVariant.Fullscreen]: DialogModal,
};

const identifyModalInstance: (m: IModalDescription<any>) => ModalIdentifier = (m) => m.nss + '.' + Date.now();

type OpenModalConfig<P extends IModalProps<O>, O = unknown> = {
  WrapperComponent: ComponentType<PropsWithChildren & IModalWrapperProps>;
  ModalComponent: ComponentType<P> | React.LazyExoticComponent<ComponentType<P>>;
  props: P;
  open: boolean;
  nss: string;
  uniqId: ModalIdentifier;
  variant: ViewVariant;
};
type ActiveModalList = Array<OpenModalConfig<IModalProps<unknown>>>;

type ModalProviderAction =
  | {
      type: 'add';
      uniqId: ModalIdentifier;
      config: IModalDescription<IModalProps<unknown>>;
      props: IModalProps<unknown>;
    }
  | {
      type: 'replace';
      uniqId: ModalIdentifier;
      config: IModalDescription<IModalProps<unknown>>;
      props: IModalProps<unknown>;
    }
  | {
      type: 'close';
      uniqId: ModalIdentifier;
    }
  | { type: 'destroy'; uniqId: ModalIdentifier }
  | { type: 'open'; uniqId: ModalIdentifier }
  | { type: 'mobile'; isMobile: boolean };

type ModalProviderState = {
  isMobile: boolean;
  defaultVariant: ViewVariant;
  modals: ActiveModalList;
};

const reducer: Reducer<ModalProviderState, ModalProviderAction> = (state, action) => {
  switch (action.type) {
    case 'mobile':
      return { ...state, isMobile: action.isMobile, defaultVariant: action.isMobile ? ViewVariant.DrawerBottom : ViewVariant.Dialog };
    case 'close':
      const modalIndex = state.modals.findIndex((m) => m.uniqId === action.uniqId);
      return modalIndex > -1
        ? {
            ...state,
            modals: state.modals.map((m, i) => (i >= modalIndex ? { ...m, open: false } : m)),
          }
        : state;
    case 'open':
      return { ...state, modals: state.modals.map((m) => (m.uniqId === action.uniqId ? { ...m, open: true } : m)) };
    case 'destroy':
      return { ...state, modals: state.modals.filter((m) => m.uniqId !== action.uniqId) };

    case 'add':
    case 'replace':
      if (state.modals.findIndex((m) => m.uniqId === action.uniqId) >= 0) {
        console.debug('Duplicated modal. State not changed', action.uniqId);
        return state;
      }
      const variant = action.config?.layout?.[state.isMobile ? 'mobile' : 'desktop'] || state.defaultVariant;
      const nextModal: OpenModalConfig<IModalProps<unknown>> = {
        nss: action.config.nss,
        uniqId: action.uniqId,
        ModalComponent: React.lazy(() => action.config.loader().then((ModalLayout) => ({ default: ModalLayout }))),
        WrapperComponent: MODAL_LAYOUTS[variant],
        open: false,
        props: action.props,
        variant,
      };
      return {
        ...state,
        modals: action.type === 'replace' ? [nextModal] : state.modals.concat([nextModal]),
      };
    default:
      return state;
  }
};

const getStats = (modals: ModalProviderState['modals']) => new Set(modals.map(({ nss, uniqId, open }) => ({ uniqId, open, nss })));

export interface IModalProviderProps {
  manageRef: React.RefObject<IModalManager | null>;
  statRef?: React.RefObject<ModalStat | null>;
  onUpdate?: (stat: ModalStat) => void;
}
export const ModalProvider: React.FC<IModalProviderProps> = ({ manageRef, statRef, onUpdate }) => {
  const isMobile = useMediaQuery<any>((theme) => theme.breakpoints.down(MODAL_MOBILE_BREAKPOINT_THRESHOLD));
  const [state, dispatch] = useReducer(reducer, {
    modals: [],
    defaultVariant: isMobile ? ViewVariant.DrawerBottom : ViewVariant.Dialog,
    isMobile,
  });

  useEffect(() => {
    dispatch({ type: 'mobile', isMobile });
  }, [isMobile]);

  useEffect(() => {
    onUpdate?.(getStats(state.modals));
  }, [state.modals, onUpdate]);

  useImperativeHandle(
    manageRef,
    () => ({
      open: (config, props, parentModal, layout) => {
        const uniqId = identifyModalInstance(config);
        dispatch({ type: parentModal ? 'add' : 'replace', uniqId, config: layout ? { ...config, layout } : (config as any), props });
        requestAnimationFrame(() => dispatch({ type: 'open', uniqId }));
        return Promise.resolve(uniqId);
      },
      close: (uniqId) => {
        dispatch({ type: 'close', uniqId });
      },
    }),
    [],
  );

  useImperativeHandle(statRef, () => getStats(state.modals), [state.modals]);

  return (
    <>
      {state.modals.map(({ WrapperComponent, ModalComponent, props, uniqId, variant, open }) => {
        const onClose = () => {
          dispatch({ type: 'close', uniqId });
          props.onCancel();
        };
        return (
          <ModalLayoutContext.Provider key={uniqId} value={{ isFullscreen: variant === ViewVariant.Fullscreen }}>
            <WrapperComponent
              ContentComponent={ModalComponent}
              contentProps={{
                ...props,
                onCancel: onClose,
              }}
              key={uniqId}
              onClose={onClose}
              onClosed={() => {
                props.onCancel();
                dispatch({ type: 'destroy', uniqId });
              }}
              open={open}
            />
          </ModalLayoutContext.Provider>
        );
      })}
    </>
  );
};
